<script>
import constants from "@/libs/Constants";
import LoginStorage from '@/libs/Login';
import Toast from "@/components/Toast.vue";
import {isValidEmail} from "@/libs/Helpers";
import ApiConnection from "@/libs/ApiConnection";
import plausible from '@/libs/PlausibleTracker';
import GroupErrorTranslation from "@/libs/locales/errors";

export default {
  components: {Toast},
  emits: ['onFeedbackSuccess', 'onFeedbackError', 'onFeedbackLoading'],
  props: {
    propClearFeedback: Boolean,
    propFeedbackFromSection: String
  },
  watch: {
    propClearFeedback(newValue, oldValue) {
      if (newValue === true)
          //Clean form when property triggers
        this.clearFeedback();
    }
  },
  data() {
    let feedbackCategoryConfig = [
      {
        title: 'General',
        value: 'general'
      },
      {
        title: 'Navigation',
        value: 'navigation'
      },
      {
        title: 'Visualization',
        value: 'visualization'
      },
      {
        title: 'Data',
        value: 'data'
      },
      {
        title: 'Other',
        value: 'other'
      }
    ];

    // Retrieve list of contacts from environment variables
    let contactSupport;
    try {
      contactSupport = JSON.parse(process.env.VUE_APP_ROUTING_FEEDBACK_CONTACTS);
    } catch (error) {
      contactSupport = [];
    }

    return {
      loading: false,
      feedbackEmail: '',
      feedbackComment: '',
      feedbackCategory: {},
      feedbackCategoryConfig: feedbackCategoryConfig,
      constants: constants,

      contactSupport: contactSupport,
      contactEmailSubject: process.env.VUE_APP_PRIVACY_FEEDBACK_EMAIL_SUBJECT
    }
  },
  methods: {
    clearFeedback() {
      this.feedbackComment = '';

      let feedbackCategory = {};
      this.feedbackCategoryConfig.forEach(configItem => {
        feedbackCategory[configItem.value] = '';
      });
      this.feedbackCategory = feedbackCategory;

      // Get e-mail from logged user
      let feedbackEmail = LoginStorage.emailRegistered;
      this.feedbackEmail = feedbackEmail !== null ? feedbackEmail : '';
    },
    onFeedbackSubmit(e) {
      e.preventDefault();
      this.loading = true;
      this.$emit('onFeedbackLoading', this.loading);

      let categories = [];
      Object.keys(this.feedbackCategory).forEach(category => {
        if (this.feedbackCategory[category] === true)
          categories.push(category)
      });

      let feedbackEmail = this.feedbackEmail.toLowerCase()
      let feedbackComment = this.feedbackComment.trim();

      ApiConnection.postUserFeedback(feedbackEmail, this.propFeedbackFromSection, categories, feedbackComment).then(response => {
        response.json().then(data => {
          //Track event
          let emailRef = LoginStorage.emailRegisteredRef;
          if (emailRef !== null)
            plausible.trackEventFeedback(emailRef);

          // Clear feedback
          this.clearFeedback();
          this.$emit('onFeedbackSuccess');
        });
      }).catch(error => {
        this.$emit('onFeedbackError', GroupErrorTranslation.t(error.message, this.$i18n));
      }).finally(() => {
        this.loading = false;
        this.$emit('onFeedbackLoading', this.loading);
      });
    },
  },
  computed: {
    feedbackCommentSize() {
      let comment = this.feedbackComment;
      if (comment === null)
        comment = '';
      return comment.length;
    },
    isFeedbackDataEmpty() {
      let feedbackHasSelected = false;
      Object.keys(this.feedbackCategory).forEach(key => {
        if (this.feedbackCategory[key] === true) {
          feedbackHasSelected = true;
          return;
        }
      })

      let comment = this.feedbackComment;
      if (comment === null)
        comment = '';
      comment = comment.trim();
      return isValidEmail(this.feedbackEmail) === false || comment === '' || !feedbackHasSelected;
    }
  },
  mounted() {
    this.clearFeedback();
  }
};
</script>
<template>
  <!-- Feedback -->
  <form @submit="onFeedbackSubmit">
    <div class="row">
      <div class="col text-muted">
        <p v-html="$t('Feedback.TellUs')"></p>
        <p v-html="$t('Feedback.Important')"></p>
        <template v-if="contactSupport.length > 0">
          <hr>
          <p class="small" v-html="$t('Feedback.Contact')"></p>
          <ul>
            <li class="small" v-for="contact in this.contactSupport">
              {{ contact.name }} <a class="link-primary"
                                    :href="`mailto:${contact.email}?subject=${contactEmailSubject}`">
              {{ contact.email }}
            </a>
            </li>
          </ul>
        </template>
      </div>
    </div>
    <hr>
    <div class="row mt-3">
      <div class="col">
        <label class="form-label fw-semibold mb-3">{{ $t('Contact.email') }} <span
            class="required">*</span></label>
        <input class="form-control" type="text" v-model="feedbackEmail" :disabled="this.loading">
      </div>
    </div>
    <div class="row mt-3">
      <div class="col">
        <label class="form-check-label fw-semibold mb-3">{{ $t('Feedback.CategoriesSelect') }} <span
            class="required">*</span></label>
        <div class="form-check form-switch" v-for="configItem in this.feedbackCategoryConfig">
          <input class="form-check-input" type="checkbox" role="switch"
                 :value="configItem.value"
                 :disabled="this.loading"
                 :id="`feedbackCat_${configItem.value}`"
                 v-model=this.feedbackCategory[configItem.value]>
          <label class="form-check-label" :for="`feedbackCat_${configItem.value}`">
            {{ $t(`Feedback.Category.${configItem.title}`) }}
          </label>
        </div>
      </div>
    </div>
    <div class="row mt-4">
      <div class="col">
        <label for="feedbackComment" class="form-label fw-semibold mb-3">{{ $t('Feedback.Comment') }}
          <span class="required">*</span></label>
        <textarea class="form-control" id="feedbackComment" rows="3" maxlength="1500"
                  v-model="feedbackComment" :disabled="this.loading"></textarea>
        <span class="text-muted-small">{{ $t('Feedback.Characters') }}: {{ feedbackCommentSize }}/1500</span>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col text-center">
        <button class="btn btn-lg btn-success" :disabled="loading || isFeedbackDataEmpty"
                type="submit">
          <div v-if="loading === true" class="spinner-grow spinner-grow-sm me-2" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <i v-else class="bi bi-send-check-fill me-2"></i>
          <span>{{ $t('Feedback.Send') }}</span>
        </button>
      </div>
    </div>
  </form>
</template>